"use client";
import Link from "next/link";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { MdOutlineDarkMode } from "react-icons/md";
import { FaLinkSlash } from "react-icons/fa6";
import { useTheme } from "@/app/theme-provider";
import useFetch from "@/app/useFetch";
import { usePathname } from "next/navigation";
import { IMG_URL } from "@/app/config";
import { get } from "@/app/helpers";
import { FaHome } from "react-icons/fa";
import { RiLetterSpacing2 } from "react-icons/ri";
import { CiImageOn } from "react-icons/ci";
import { IoIosContrast } from "react-icons/io";
import { FaTextHeight } from "react-icons/fa";
import { RxColumnSpacing } from "react-icons/rx";
import { MdAnimation } from "react-icons/md";
import { MdBrightness4 } from "react-icons/md";
import { AiOutlineColumnHeight } from "react-icons/ai";
import { FaArrowsLeftRightToLine } from "react-icons/fa6";
import { FaPhone, FaWhatsapp } from "react-icons/fa6";
import { MdOutlineEmail } from "react-icons/md";
import { BsHeadset } from "react-icons/bs";
const Header = () => {
  const pathname = usePathname();
  const {
    theme,
    toggleTheme,
    toggleHighlightLinks,
    highlightLinks,
    language,
    toggleLanguage,
    toggleAccessibilityMode,
    accessibilityMode,
    hideImages,
    toggleHideImages,
    contrastMode,
    toggleContrast,
    textSize,
    toggleTextSize,
    spacing,
    toggleSpacing,
    pauseAnim,
    togglePauseAnimation,
    saturation,
    toggleSaturation,
    lineHeight,
    toggleLineHeight,
    align,
    toggleAlign,
  } = useTheme();

  const [openMenu, setOpenMenu] = useState(null);
  const [activeSub, setActiveSub] = useState(null);
  const [showAccess, setShowAccess] = useState(false);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [isScrolled, setIsScrolled] = useState(false);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [shouldLoadMenuData, setShouldLoadMenuData] = useState(false);
  const searchRef = useRef(null);

  const primeMenuData = () => setShouldLoadMenuData(true);

  useEffect(() => {
    setQuery("");
    setShowResults(false);
  }, [pathname]);

  useEffect(() => {
    if (shouldLoadMenuData) {
      return;
    }

    const loadMenuData = () => setShouldLoadMenuData(true);

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(loadMenuData, { timeout: 1500 });

      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(loadMenuData, 800);
    return () => window.clearTimeout(timeoutId);
  }, [shouldLoadMenuData]);

  useEffect(() => {
    const fetchResults = async () => {
      if (query.trim().length < 2) {
        setResults([]);
        setShowResults(false);
        return;
      }

      try {
        const res = await get(
          `/website/search?words=${encodeURIComponent(query)}`
        );
        setResults(res);
        setShowResults(true);
      } catch (error) {
        console.error("Error fetching search results:", error);
      }
    };

    const delayDebounce = setTimeout(fetchResults, 300);
    return () => clearTimeout(delayDebounce);
  }, [query]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const nextIsScrolled = window.scrollY > 1;
      setIsScrolled((prev) => (prev === nextIsScrolled ? prev : nextIsScrolled));
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  //  const [page, setPage] = useState(1);
  const {
    data: specialcells,
  } = useFetch(
    shouldLoadMenuData ? "/website/contacts-specialcells" : null,
    // { page, limit: 5 }, // pass page & limit here
    []
  );

  const { data: ranges } = useFetch(
    shouldLoadMenuData ? "/website/contacts-ranges" : null,
    // { page, limit: 5 }, // pass page & limit here
    []
  );

  const { data: wings } = useFetch(
    shouldLoadMenuData ? "/website/contacts-specialwings" : null,
    // { page, limit: 5 }, // pass page & limit here
    []
  );

  const { data: units } = useFetch(
    shouldLoadMenuData ? "/website/contacts-units" : null,
    // { page, limit: 5 }, // pass page & limit here
    []
  );

  const { data: awards } = useFetch(
    shouldLoadMenuData ? "/website/vigilance-awards-all" : null,
    // { page, limit: 5 }, // pass page & limit here
    []
  );

  const { data: logos } = useFetch(
    "/website/header-sections",
    // { page, limit: 5 }, // pass page & limit here
    {}, [], null
  );

  const { data: customMenuData } = useFetch(
    shouldLoadMenuData ? "/website/custom-menu" : null,
    [],
    []
  );


  const convertSubMenuItem = (item) => {
    const menuItem = {
      title: { en: item.title, ml: item.title },
    };

    if (item.link_type === "custompage" && item.permalink) {
      menuItem.link = `/page/${item.permalink}`;
    } else if (item.link_type === "url" && item.external_link) {
      menuItem.link = item.external_link;
      menuItem.external = true; // Flag for external links
    }

    if (item.children && item.children.length > 0) {
      menuItem.submenu = item.children.map(convertSubMenuItem);
    }

    return menuItem;
  };

  // Merge custom menu data with fixed menu structure

  const handleAccAction = (action) => {
    action();
    setShowAccess(false);
  };


  const mergeCustomMenu = () => {
    const fixedMenuData = [
      {
        title: { en: "Vigilance", ml: "വിജിലൻസ്" },
        submenu: [
          {
            title: { en: "Awards", ml: "അവാർഡുകൾ" },
            submenu: awards?.length
              ? awards.map((item) => ({
                title: {
                  en: item.name, // English name directly from API
                  ml: item.name || item.name, // Malayalam name (fallback to English if not provided)
                },
                link: `/vigilance/awards/${item.permalink}`,
              }))
              : [
                {
                  title: { en: "Loading...", ml: "ലോഡുചെയ്യുന്നു..." },
                  link: "#",
                },
              ],
          },
          {
            title: { en: "Who is Who", ml: " യൂണിറ്റ് മേധാവികള്‍" },
            link: "/vigilance/who-is-who",
          },
          {
            title: { en: "Former Chiefs", ml: "മുൻ മേധാവികൾ" },
            link: "/vigilance/former-chiefs",
          },
        ],
      },
      {
        title: { en: "Wings", ml: "വിഭാഗങ്ങൾ" },
        submenu: [
          {
            title: { en: "Directorate", ml: "ഡയറക്ടറേറ്റ്" },
            link: "/wings/directorate",
          },
          {
            title: { en: "Ranges & Units", ml: "റേഞ്ചുകളും യൂണിറ്റുകളും" },
            link: "/wings/ranges-and-units",
          },
          {
            title: {
              en: "Special Investigation Units",
              ml: "സ്പെഷ്യൽ ഇൻവെസ്റ്റിഗേഷൻ യൂണിറ്റുകൾ",
            },
            link: "/wings/siu-wings",
          },
          {
            title: { en: "Special Cells", ml: "സ്പെഷ്യൽ സെല്ലുകൾ" },
            link: "/wings/special-cells",
          },
          {
            title: { en: "Legal Wings", ml: "നിയമ വിഭാഗങ്ങള്‍" },
            submenu: [
              {
                title: { en: "Legal Advisor", ml: "നിയമ ഉപദേഷ്ടാവ്" },
                link: "/wings/legal-advisor",
              },
              {
                title: {
                  en: "Public Prosecutor",
                  ml: "പബ്ലിക് പ്രോസിക്യൂട്ടർ",
                },
                link: "/wings/public-prosecutor",
              },
            ],
          },
          {
            title: {
              en: " Courts & Tribunals",
              ml: " കോടതികളും ട്രൈബ്യൂണലുകളും",
            },
            link: "/wings/courts-tribunals-wings",
          },
          {
            title: { en: "Organisation Chart", ml: " ഓര്‍ഗനൈസേഷന്‍ ചാര്‍ട്ട്" },
            link: "/chart",
          },
        ],
      },
      {
        title: { en: "General Info", ml: "പൊതുവിവരം" },
        submenu: [
          {
            title: { en: "E-Services", ml: "ഇ-സേവനങ്ങൾ" },
            submenu: [
              {
                title: { en: "FIR Online", ml: "എഫ്.ഐ.ആർ ഓൺലൈൻ" },
                link: "/fir-online",
              },
              {
                title: {
                  en: "Vigilance Court Case Management System",
                  ml: "വിജിലൻസ് കോടതി കേസ് മാനേജ്മെന്റ് സിസ്റ്റം",
                },
                link: "/vigilance-court-case-management-system",
              },
              {
                title: { en: "E-Bulletin", ml: "ഇ-ബുള്ളറ്റിൻ" },
                link: "/e-bulletin",
              },
            ],
          },
          {
            title: { en: "Gallery", ml: "ഗാലറി" },
            link: "/gallery",
          },
          {
            title: { en: "Press Release", ml: "പ്രസ് റിലീസ്" },
            link: "/press-release",
          },
          {
            title: {
              en: "Prevention Of Sexual Harassment",
              ml: "ലൈംഗിക പീഡനം തടയൽ",
            },
            link: "/posh",
          },
          {
            title: { en: " Surprise checks", ml: " മിന്നൽ പരിശോധനകൾ" },
            link: "/surprise-check",
          },
          {
            title: {
              en: "Case Statistics",
              ml: " കേസുകളുടെ സ്ഥിതിവിവരക്കണക്കുകള്‍ ",
            },
            link: "/case-statistics",
          },
          {
            title: { en: " Right to Information", ml: " വിവരാവകാശം" },
            link: "/right-to-information",
          },
          {
            title: { en: "Right to Service", ml: " സേവനാവകാശം" },
            link: "/right-to-service",
          },
          {
            title: {
              en: " CMO Portal Charge Officers",
              ml: " സി എം ഒ പോർട്ടല്‍ ചുമതലയുള്ള ഉദ്യോഗസ്ഥർ",
            },
            link: "/cmo-portal",
          },
          {
            title: { en: " Official Vocabulary", ml: " ഔദ്യോഗിക പദാവലി" },
            link: "/official-vocabulary",
          },
          {
            title: {
              en: " Creativity of VACB Staff",
              ml: " VACB ജീവനക്കാരുടെ സർഗാത്മകത",
            },
            link: "/creativity-of-vacb-staff",
          },
          {
            title: { en: "FAQs", ml: " പതിവ് ചോദ്യങ്ങള്‍" },
            link: "/faq",
          },
        ],
      },
      {
        title: { en: "Contact", ml: "സമ്പർക്കം" },
        submenu: [
          {
            title: { en: "Directorate", ml: "ഡയറക്ടറേറ്റ്" },
            link: "/contact/directorate",
          },

          {
            title: { en: "Units", ml: "യൂണിറ്റുകൾ" },
            submenu: units?.length
              ? units.map((item) => ({
                title: {
                  en: item.name, // English name directly from API
                  ml: item.name || item.name, // Malayalam name (fallback to English if not provided)
                },
                link: `/contact/units/${item.permalink}`,
              }))
              : [
                {
                  title: { en: "Loading...", ml: "ലോഡുചെയ്യുന്നു..." },
                  link: "#",
                },
              ],
          },

          {
            title: { en: "Ranges", ml: "റേഞ്ചുകൾ" },
            link: "/",
            submenu: ranges?.length
              ? ranges.map((item) => ({
                title: {
                  en: item.name, // English name directly from API
                  ml: item.name || item.name, // Malayalam name (fallback to English if not provided)
                },
                link: `/contact/ranges/${item.permalink}`,
              }))
              : [
                {
                  title: { en: "Loading...", ml: "ലോഡുചെയ്യുന്നു..." },
                  link: "#",
                },
              ],
          },
          {
            title: { en: "Special Wings", ml: " സ്പെഷ്യല്‍ വിങ്" },
            link: "/",
            submenu: wings?.length
              ? wings.map((item) => ({
                title: {
                  en: item.name, // English name directly from API
                  ml: item.name || item.name, // Malayalam name (fallback to English if not provided)
                },
                link: `/contact/special-wing/${item.permalink}`,
              }))
              : [
                {
                  title: { en: "Loading...", ml: "ലോഡുചെയ്യുന്നു..." },
                  link: "#",
                },
              ],
          },

          {
            title: { en: "Special Cells", ml: "സ്പെഷ്യല്‍ സെല്‍" },
            link: "/",
            submenu: specialcells?.length
              ? specialcells.map((item) => ({
                title: {
                  en: item.name, // English name directly from API
                  ml: item.name || item.name, // Malayalam name (fallback to English if not provided)
                },
                link: `/contact/special-cells/${item.permalink}`,
              }))
              : [
                {
                  title: { en: "Loading...", ml: "ലോഡുചെയ്യുന്നു..." },
                  link: "#",
                },
              ],
          },

          {
            title: { en: "Legal Wings", ml: "നിയമ വിഭാഗങ്ങള്‍" },
            submenu: [
              {
                title: {
                  en: "Legal Advisor",
                  ml: "നിയമ ഉപദേഷ്ടാവ്",
                },
                link: "/contact/legal-advisor",
              },
              {
                title: {
                  en: "Public Prosecutor",
                  ml: "പബ്ലിക് പ്രോസിക്യൂട്ടർ",
                },
                link: "/contact/public-prosecutor",
              },
            ],
          },
        ],
      },
      {
        title: { en: "Tenders", ml: "ടെൻഡറുകൾ" },
        submenu: [
          {
            title: { en: "Tenders/Quotations", ml: "ടെൻഡറുകൾ/ക്വട്ടേഷനുകൾ" },
            link: "/tenders",
          },
        ],
      },
      {
        title: { en: "Downloads", ml: "ഡൗൺലോഡുകൾ" },

        submenu: [
          {
            title: { en: "Acts & Rules", ml: "നിയമങ്ങളും ചട്ടങ്ങളും" },
            link: "/downloads/act-and-rules",
          },
          {
            title: { en: "Orders", ml: "ഉത്തരവുകൾ" },
            submenu: [
              {
                title: { en: "D G O", ml: "ഡി ജി ഒ" },
                link: "/downloads/orders/d-g-o",
              },
              {
                title: {
                  en: "SOs And Proceedings",
                  ml: " സേവന ഉത്തരവുകളും നടപടിക്കുറിപ്പുകളും",
                },
                link: "/downloads/orders/sos-and-proceedings",
              },
              {
                title: {
                  en: "Government Orders",
                  ml: " ഗവൺമെന്റ് ഉത്തരവുകള്‍",
                },
                link: "/downloads/orders/government-orders",
              },
              {
                title: { en: "Exam Details", ml: "പരീക്ഷയുടെ വിശദാംശങ്ങൾ" },
                link: "/downloads/orders/exam-details",
              },
            ],
          },
          {
            title: { en: "Seniority List", ml: "സീനിയോറിറ്റി ലിസ്റ്റ്" },
            link: "/downloads/seniority-list",
          },
          { title: { en: "Forms", ml: "ഫോംസ്" }, link: "/downloads/forms" },
        ],
      },
    ];

    // Map API menu titles to fixed menu titles (case-insensitive)
    const menuTitleMap = {
      vigilance: "Vigilance",
      wings: "Wings",
      generalinfo: "General Info",
      "general info": "General Info",
      contact: "Contact",
      tenders: "Tenders",
      downloads: "Downloads",
    };

    // Create a map of custom menu data by title (lowercase for matching)
    const customMenuMap = {};
    if (customMenuData && Array.isArray(customMenuData)) {
      customMenuData.forEach((menuItem) => {
        const normalizedTitle = menuItem.title?.toLowerCase().trim();
        if (normalizedTitle && menuItem.subMenus) {
          customMenuMap[normalizedTitle] =
            menuItem.subMenus.map(convertSubMenuItem);
        }
      });
    }


    const mergedMenuData = fixedMenuData.map((menu) => {
      const menuTitle = menu.title.en;
      const normalizedTitle = menuTitle.toLowerCase().trim();

      let customSubMenus = null;

      if (customMenuMap[normalizedTitle]) {
        customSubMenus = customMenuMap[normalizedTitle];
      } else {
        // Check menuTitleMap for variations
        const mappedTitle = Object.keys(menuTitleMap).find(
          (key) => menuTitleMap[key] === menuTitle
        );
        if (mappedTitle && customMenuMap[mappedTitle]) {
          customSubMenus = customMenuMap[mappedTitle];
        } else {
          // Try reverse lookup - find API title that maps to this menu
          const apiTitle = Object.keys(customMenuMap).find((key) => {
            const mappedValue = menuTitleMap[key];
            return (
              mappedValue === menuTitle ||
              key === normalizedTitle ||
              key.replace(/\s+/g, "") === normalizedTitle.replace(/\s+/g, "")
            );
          });
          if (apiTitle) {
            customSubMenus = customMenuMap[apiTitle];
          }
        }
      }

      if (customSubMenus && customSubMenus.length > 0) {
        return {
          ...menu,
          // submenu: [...(menu.submenu || []), ...customSubMenus],
          submenu: [...customSubMenus, ...(menu.submenu || [])],

        };
      }

      return menu;
    });

    return mergedMenuData;
  };

  const menuData = useMemo(
    () => mergeCustomMenu(),
    [awards, customMenuData, ranges, specialcells, units, wings]
  );

  function getCurrentFormattedTime() {
    const now = new Date();

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const dayName = days[now.getDay()];
    const date = now.getDate();
    const monthName = months[now.getMonth()];
    const year = now.getFullYear();

    const hour24 = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const displayHours = (hour24 % 12) || 12;

    const dateString = `${dayName} ${date}${getOrdinalSuffix(date)} ${monthName} ${year}`;
    const timeString = `${displayHours}:${minutes} ${ampm}`;

    return { dateString, timeString };
  }


  function getOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  }


  const [clock, setClock] = useState({ dateString: "", timeString: "" });

  useEffect(() => {
    const updateClock = () => setClock(getCurrentFormattedTime());
    updateClock();
    const intervalId = setInterval(updateClock, 30000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <>
      <header className={isScrolled ? "scrolled" : ""}>
        <nav className="cmpad container-fluid">
          <div className="menubar">
            <div>
              <Link
                href="/"
                className="navbar-brand d-flex align-items-center pt-0"
              >
                { logos  && (
                  <>
                    {theme === "light" ? (
                      <img
                        src={`${IMG_URL}${logos?.logolight}`}
                        alt="Logo"
                        className="logo"
                      />
                    ) : (
                      <img
                        src={`${IMG_URL}${logos?.logodark}`}
                        alt="Logo"
                        className="logo"
                      />
                    )}
                  </>
                )}


              </Link>
            </div>

            <div className="right-nav position-relative">
              {/* Top bar */}
              <div className="right-top">
                <div className="date">
                  <img src="/icons/clock.svg" alt="clock" />
                  <p className="mb-0">
                    {clock.dateString} <span>{clock.timeString}</span>
                    {/* Wednesday 26th February 2025 <span>11:45:26 AM</span> */}
                  </p>
                </div>
                <div className="date">
                  <img src="/icons/language.svg" alt="language" />
                  <p
                    style={{ cursor: "pointer" }}
                    onClick={toggleLanguage}
                    className="mb-0"
                  >
                    {language === "en" ? "മലയാളം" : "English"}
                  </p>
                </div>
                <div
                  onClick={() => setShowAccess((prev) => !prev)}
                  className="date access"
                >
                  <img className="access-keep" src="/icons/men.svg" alt="menicon" />
                </div>
                <a href="/file-complaint">
                  <button className="complaint-btn heading-font">
                    <img src="/icons/complaint.svg" alt="complaint" />
                    File a Complaint
                  </button>
                </a>
              </div>

              {/* Main Menu */}
              <div className="right-bottom  mt-2">
                <Link aria-label="home" href="/">
                  <FaHome />
                </Link>
                <ul className="arrow-menu" onMouseEnter={primeMenuData}>
                  {menuData.map((menu, idx) => (
                    <li
                      key={idx}
                      className="li-link"
                      onMouseEnter={() => setOpenMenu(menu.title.en)}
                      onMouseLeave={() => {
                        setOpenMenu(null);
                        setActiveSub(null);
                      }}
                    >
                      {/* Visible Title */}
                      <span className="menu-title">{menu.title[language]}</span>

                      {/* Make entire <li> clickable if link exists */}
                      {menu.link && (
                        <Link href={menu.link} className="full-link"></Link>
                      )}

                      {/* Mega Menu */}
                      {menu.submenu && (
                        <div
                          data-lenis-prevent
                          className={`mega-menu ${openMenu === menu.title.en ? "open" : ""
                            }`}
                        >
                          {/* Left column */}
                          <ul className="menu-list">
                            {menu.submenu.map((sub, i) => (
                              <li
                                key={i}
                                className={`menu-item ${activeSub === sub.title.en ? "active" : ""
                                  }`}
                                onMouseEnter={() =>
                                  sub.submenu
                                    ? setActiveSub(sub.title.en)
                                    : setActiveSub(null)
                                }
                              >
                                {/* Submenu Text */}
                                <span className="submenu-title">
                                  {sub.title[language]}
                                </span>

                                {/* Make submenu <li> clickable */}
                                {sub.link && (
                                  sub.external ? (
                                    <a
                                      href={sub.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="full-link"
                                    ></a>
                                  ) : (
                                    <Link href={sub.link} className="full-link"></Link>
                                  )
                                )}

                                {/* Arrow */}
                                {sub.submenu && <span className="arrow">›</span>}
                              </li>
                            ))}
                          </ul>

                          {/* Right column */}
                          {menu.submenu.some((s) => s.title.en === activeSub) && (
                            <ul
                              className="submenu-list"
                              onMouseEnter={() => setActiveSub(activeSub)}
                              onMouseLeave={() => setActiveSub(null)}
                            >
                              {menu.submenu
                                .find((s) => s.title.en === activeSub)
                                ?.submenu?.map((inner, j) => (
                                  <li key={j} className="submenu-item li-link">
                                    <span className="inner-sub-span">{inner.title[language]}</span>

                                    {/* Make inner submenu <li> clickable */}
                                    {inner.link && (
                                      inner.external ? (
                                        <a
                                          href={inner.link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="full-link"
                                        ></a>
                                      ) : (
                                        <Link href={inner.link} className="full-link"></Link>
                                      )
                                    )}
                                  </li>
                                ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>


                <div ref={searchRef} className="search-box">
                  <img className="search-icon" src="/icons/search.svg" alt="search" />
                  <input
                    placeholder="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => query?.length > 1 && setShowResults(true)}
                  />

                  {showResults && results?.length > 0 && (
                    <div className="serchbx">
                      <ul>
                        {results.map((ite, index) => {
                          const safeUrl = ite.search_url.startsWith("/")
                            ? ite.search_url
                            : `/${ite.search_url}`;

                          return (
                            <li key={index}>
                              <Link href={safeUrl}>{ite.search_head}</Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Accessibility Box */}
              <div
                data-lenis-prevent
                className={`accessbox ${showAccess ? "show" : ""}`}
              >
                {/* HEADER */}
                <div className="hdbox">
                  <p className="head">Accessibility Menu</p>
                  <button
                    className="closebtn"
                    onClick={() => setShowAccess(false)}
                  >
                    <img src="/icons/close.svg" alt="Close" />
                  </button>
                </div>

                {/* ACTION LIST */}
                <div className="acclist">

                  {/* Helper function */}


                  <div
                    onClick={() => handleAccAction(toggleLanguage)}
                    className="accbtn darkbtn"
                  >
                    <img className="icon-acces" src="/icons/language.svg" alt="language" />
                    {language === "en" ? "Malayalam" : "English"}
                  </div>

                  <div
                    onClick={() => handleAccAction(toggleTheme)}
                    className="accbtn darkbtn"
                  >
                    <MdOutlineDarkMode className="icon-acces" />
                    {theme === "dark" ? "Light Mode" : "Dark Mode"}
                  </div>

                  <div
                    onClick={() => handleAccAction(toggleHighlightLinks)}
                    className="accbtn darkbtn"
                  >
                    <FaLinkSlash className="icon-acces" />
                    {highlightLinks ? "Remove Highlight" : "Highlight Links"}
                  </div>

                  <div
                    onClick={() => handleAccAction(toggleAccessibilityMode)}
                    className="accbtn darkbtn"
                  >
                    <RiLetterSpacing2 className="icon-acces" />
                    {accessibilityMode === "cursor" && "Big Cursor"}
                    {accessibilityMode === "bigcursor" && "Reading Mask"}
                    {accessibilityMode === "mask" && "Reading Guide"}
                    {accessibilityMode === "guide" && "Cursor"}
                  </div>

                  <div
                    onClick={() => handleAccAction(toggleHideImages)}
                    className="accbtn darkbtn"
                  >
                    <CiImageOn className="icon-acces" />
                    {hideImages ? "Show Images" : "Hide Images"}
                  </div>

                  <div
                    onClick={() => handleAccAction(toggleContrast)}
                    className="accbtn darkbtn"
                  >
                    <IoIosContrast className="icon-acces" />
                    {contrastMode === "normal" && "Dark Contrast"}
                    {contrastMode === "dark" && "Light Contrast"}
                    {contrastMode === "light" && "Invert Colors"}
                    {contrastMode === "invert" && "Normal Contrast"}
                  </div>

                  <div
                    onClick={() => handleAccAction(toggleTextSize)}
                    className="accbtn darkbtn"
                  >
                    <FaTextHeight className="icon-acces" />
                    {textSize === "normal" && "Medium Text"}
                    {textSize === "medium" && "Large Text"}
                    {textSize === "large" && "Extra Large Text"}
                    {textSize === "xlarge" && "Normal Text"}
                  </div>

                  <div
                    onClick={() => handleAccAction(toggleSpacing)}
                    className="accbtn darkbtn"
                  >
                    <RxColumnSpacing className="icon-acces" />
                    {spacing === "normal" && "Medium Spacing"}
                    {spacing === "medium" && "Wide Spacing"}
                    {spacing === "wide" && "Normal Spacing"}
                  </div>

                  <div
                    onClick={() => handleAccAction(togglePauseAnimation)}
                    className="accbtn darkbtn"
                  >
                    <MdAnimation className="icon-acces" />
                    {pauseAnim ? "Enable Animations" : "Pause Animations"}
                  </div>

                  <div
                    onClick={() => handleAccAction(toggleSaturation)}
                    className="accbtn darkbtn"
                  >
                    <MdBrightness4 className="icon-acces" />
                    {saturation === "normal" && "Low Saturation"}
                    {saturation === "low" && "High Saturation"}
                    {saturation === "high" && "Desaturate"}
                    {saturation === "desaturate" && "Normal Saturation"}
                  </div>

                  <div
                    onClick={() => handleAccAction(toggleLineHeight)}
                    className="accbtn darkbtn"
                  >
                    <AiOutlineColumnHeight className="icon-acces" />
                    {lineHeight === "normal" && "Line Height 1.5"}
                    {lineHeight === "1.5" && "Line Height 1.7"}
                    {lineHeight === "1.7" && "Line Height 2"}
                    {lineHeight === "2" && "Normal Line Height"}
                  </div>

                  <div
                    onClick={() => handleAccAction(toggleAlign)}
                    className="accbtn darkbtn"
                  >
                    <FaArrowsLeftRightToLine className="icon-acces" />
                    {align === "left" && "Center Align"}
                    {align === "center" && "Right Align"}
                    {align === "right" && "Left Align"}
                  </div>

                </div>
              </div>

            </div>
            <div className="mob-file-button">
              <div className="date date-mob">
                <img src="/icons/language.svg" alt="language" />
                <p
                  style={{ cursor: "pointer", color: "#ff9500" }}
                  onClick={toggleLanguage}
                  className="mb-0"
                >
                  {language === "en" ? "മ" : "En"}
                </p>
              </div>
              <Link className="file-mobile-menu" href="/file-complaint">
                <button className="complaint-btn heading-font">
                  <img src="/icons/complaint.svg" alt="icon" />
                  File a Complaint
                </button>
              </Link>
              <div
                onClick={() => setShowAccess((prev) => !prev)}
                className="date access date-mob"
              >
                <img src="/icons/men.svg" alt="men-icon" />
              </div>

              <div
                onClick={() => {
                  primeMenuData();
                  setIsMenuOpen(true);
                }}
                className="mobile-menu"
              >
                <img src={"/icons/mobilemenu.svg"} alt="mobilemenu" />
              </div>
              <div data-lenis-prevent className={`accessbox  ${showAccess ? "show" : ""}`}>
                <div className="hdbox">
                  <p className="head">Accessibility Menu</p>
                  <button
                    className="closebtn"
                    onClick={() => setShowAccess(false)}
                  >
                    <img src="/icons/close.svg" alt="Close" />
                  </button>
                </div>
                <div className="acclist ">
                  <div onClick={() => handleAccAction(toggleLanguage)} className="accbtn darkbtn">
                    <img className="icon-acces" src="/icons/language.svg" alt="language" />
                    {language === "en" ? "Malayalam" : "English"}
                  </div>
                  <div onClick={toggleTheme} className="accbtn darkbtn">
                    <MdOutlineDarkMode className="icon-acces" />
                    {theme === "dark" ? "Light Mode" : "Dark Mode"}
                  </div>
                  <div
                    onClick={toggleHighlightLinks}
                    className="accbtn darkbtn"
                  >
                    <FaLinkSlash className="icon-acces" />
                    {highlightLinks ? "Remove Highlight" : "Highlight Links"}
                  </div>

                  <div onClick={toggleAccessibilityMode} className="accbtn darkbtn">
                    <RiLetterSpacing2 className="icon-acces" />
                    {accessibilityMode === "cursor" && "Big Cursor"}
                    {accessibilityMode === "bigcursor" && "Reading Mask"}
                    {accessibilityMode === "mask" && "Reading Guide"}
                    {accessibilityMode === "guide" && "Cursor"}
                  </div>

                  <div onClick={toggleHideImages} className="accbtn darkbtn">
                    <CiImageOn className="icon-acces" />
                    {hideImages ? "Show Images" : "Hide Images"}
                  </div>
                  <div onClick={toggleContrast} className="accbtn darkbtn">
                    <IoIosContrast className="icon-acces" />

                    {contrastMode === "normal" && "Dark Contrast"}
                    {contrastMode === "dark" && "Light Contrast"}
                    {contrastMode === "light" && "Invert Colors"}
                    {contrastMode === "invert" && "Normal Contrast"}
                  </div>

                  <div onClick={toggleTextSize} className="accbtn darkbtn">
                    <FaTextHeight className="icon-acces" />

                    {textSize === "normal" && "Medium Text"}
                    {textSize === "medium" && "Large Text"}
                    {textSize === "large" && "Extra Large Text"}
                    {textSize === "xlarge" && "Normal Text"}
                  </div>

                  <div onClick={toggleSpacing} className="accbtn darkbtn">
                    <RxColumnSpacing className="icon-acces" />

                    {spacing === "normal" && "Medium Spacing"}
                    {spacing === "medium" && "Wide Spacing"}
                    {spacing === "wide" && "Normal Spacing"}
                  </div>

                  <div onClick={togglePauseAnimation} className="accbtn darkbtn">
                    <MdAnimation className="icon-acces" />

                    {pauseAnim ? "Enable Animations" : "Pause Animations"}
                  </div>

                  <div onClick={toggleSaturation} className="accbtn darkbtn">
                    <MdBrightness4 className="icon-acces" />

                    {saturation === "normal" && "Low Saturation"}
                    {saturation === "low" && "High Saturation"}
                    {saturation === "high" && "Desaturate"}
                    {saturation === "desaturate" && "Normal Saturation"}
                  </div>

                  <div onClick={toggleLineHeight} className="accbtn darkbtn">
                    <AiOutlineColumnHeight className="icon-acces" />

                    {lineHeight === "normal" && "Line Height 1.5"}
                    {lineHeight === "1.5" && "Line Height 1.7"}
                    {lineHeight === "1.7" && "Line Height 2"}
                    {lineHeight === "2" && "Normal Line Height"}
                  </div>

                  <div onClick={toggleAlign} className="accbtn darkbtn">
                    <FaArrowsLeftRightToLine className="icon-acces" />

                    {align === "left" && "Center Align"}
                    {align === "center" && "Right Align"}
                    {align === "right" && "Left Align"}
                  </div>

                </div>
              </div>
            </div>
          </div>
        </nav>
      </header>
      <div className={`mobile-side-bar ${isMenuOpen ? "active" : ""}`}>
        <button className="close-btn" onClick={() => setIsMenuOpen(false)}>
          ✕
        </button>
        <Link className="mob-file" href="/file-complaint">
          <button className="complaint-btn heading-font">
            <img src="/icons/complaint.svg" alt="icon" />
            File a Complaint
          </button>
        </Link>
        <div className="mobile-header mt-3">
          <Link href="/" aria-label="home">
            <FaHome />
          </Link>
        </div>

        <div className="mobile-drawer-actions">
          <Link href="/file-complaint">
            <button className="complaint-btn heading-font">
              <img src="/icons/complaint.svg" alt="icon" />
              File a Complaint
            </button>
          </Link>
          <button className="mobile-language-btn" type="button" onClick={toggleLanguage}>
            <img src="/icons/language.svg" alt="language" />
            <span>{language === "en" ? "മാ" : "En"}</span>
          </button>
        </div>

      

        <ul className="mobile-menu-list">
          {menuData.map((menu, idx) => (
            <li key={idx}>
              <div
                className="mobile-menu-item"
                onClick={() => setOpenMenu(openMenu === idx ? null : idx)}
              >
                <span>{menu.title[language]}</span>
                {menu.submenu && (
                  <span className="arrow">{openMenu === idx ? "−" : "+"}</span>
                )}
              </div>

              {/* Level 1 submenu */}
              {menu.submenu && openMenu === idx && (
                <ul className="mobile-submenu">
                  {menu.submenu.map((sub, sIdx) => (
                    <li key={sIdx}>
                      <div
                        className="mobile-submenu-item"
                        onClick={() =>
                          setActiveSub(
                            activeSub === `${idx}-${sIdx}`
                              ? null
                              : `${idx}-${sIdx}`
                          )
                        }
                      >
                        {sub.link ? (
                          sub.external ? (
                            <a
                              href={sub.link}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {sub.title[language]}
                            </a>
                          ) : (
                            <Link href={sub.link}>{sub.title[language]}</Link>
                          )
                        ) : (
                          <span>{sub.title[language]}</span>
                        )}
                        {sub.submenu && (
                          <span className="arrow">
                            {activeSub === `${idx}-${sIdx}` ? "−" : "+"}
                          </span>
                        )}
                      </div>

                      {/* Level 2 submenu */}
                      {sub.submenu && activeSub === `${idx}-${sIdx}` && (
                        <ul className="mobile-inner-submenu">
                          {sub.submenu.map((inner, iIdx) => (
                            <li key={iIdx}>
                              {inner.link ? (
                                inner.external ? (
                                  <a
                                    href={inner.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {inner.title[language]}
                                  </a>
                                ) : (
                                  <Link href={inner.link}>
                                    {inner.title[language]}
                                  </Link>
                                )
                              ) : (
                                <span>{inner.title[language]}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
          <div className="mobile-contact-links">
          <a href="tel:1064">
            <span className="mobile-contact-icon icon-phone"><FaPhone /></span>
            <span>1064</span>
          </a>
          <a href="mailto:vig.vacb@kerala.gov.in">
            <span className="mobile-contact-icon icon-email"><MdOutlineEmail /></span>
            <span>vig.vacb@kerala.gov.in</span>
          </a>
          <a href="https://wa.me/9447789100" target="_blank" rel="noopener noreferrer">
            <span className="mobile-contact-icon icon-whatsapp"><FaWhatsapp /></span>
            <span>9447789100</span>
          </a>
          <a href="#">
            <span className="mobile-contact-icon icon-support"><BsHeadset /></span>
            <span>Working Time : 10.15 AM - 5:15 PM</span>
          </a>
        </div>
        
      </div>
    </>
  );
};

export default Header;
